import type { FilterItem } from "@repo/models";

import { KeyedArray } from "@repo/models";
import { mapToKeyedArray } from "@repo/models/filterItem";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

export { QUERY_KEYS } from "./queryKeys";
export { BRANDS, BRANCHES, CHANNELS, MARKETERS, STATES, GET_USER_INFO } from "./queryKeys";
export { COMPANIES, DIVISIONS, REGIONS, MARKETS } from "./queryKeys";

// Backward-compat constants are re-exported from queryKeys.ts
// BRANCHES, CHANNELS, BRANDS, MARKETERS, STATES

export function useFilterItemQuery<T extends FilterItem = FilterItem>(
	name: string[],
	api: (...params) => Promise<(T | FilterItem)[]>,
	...params
) {
	const query = useQuery({ queryKey: [...name, ...params], queryFn: callApi });
	const data = useMemo(() => query.data ?? emptyArray, [query.data]);
	const loaded = query.isFetched;

	return [data, loaded] as [KeyedArray<T>, boolean];

	async function callApi() {
		return await api(...params).then(mapToKeyedArray);
	}
}

const emptyArray = new KeyedArray<FilterItem>([], (k) => k.id);

export function useQueryWrapper<T>(
	key: string | string[],
	triggers: any[],
	apiFn: (context?: any) => Promise<T>,
	options?: any,
) {
	const queryClient = useQueryClient();
	const keyArr = typeof key === "string" ? [key] : key;
	if (!keyArr?.length) throw new Error("No valid key for query");
	const query = useQuery<T>({
		queryKey: [...keyArr, ...triggers],
		queryFn: (context) => apiFn(context),
		...options,
	});
	const loaded = query.isFetched;
	const loading = query.isFetching;
	const keyJson = JSON.stringify(keyArr);
	const clearQuery = useCallback(
		() => queryClient.invalidateQueries({ queryKey: JSON.parse(keyJson) as string[] }),
		[queryClient, keyJson],
	);

	return { data: query.data, loaded, clearQuery, loading };
}
